import { Theme, styled } from "@mui/material";
import AcxExpansion from "components/UI/AcxExpansion";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import React, { useCallback } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useLocation, useNavigate } from "react-router";
import theme from "../../Theme/AppTheme";
import NavigationLink from "./NavigationLink";
import NavigationStore, { IItemOptions, INavigation } from "./NavigationStore";
import { observer } from "mobx-react";

const RootDiv = styled("div")(({ theme }) => ({
    height: "100%",
    paddingTop: theme.spacing(1.75),
}));

const groupHeaderStyle = (theme: Theme) => ({
    fontSize: "16px",
    fontFamily: "Inter",
    fontWeight: "bold",
    color: theme.palette.black.main,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
});

const itemNavigationBodyStyle = (theme: Theme) => ({
    paddingRight: theme.spacing(0.5),
    paddingLeft: theme.spacing(1.75),
});

const itemNavigationRootStyle = (theme: Theme) => ({
    paddingRight: theme.spacing(1.5),
    paddingLeft: theme.spacing(1.5),
});

interface NavigationProps {
    store: NavigationStore;
    itemOptions?: IItemOptions;
    controls?: React.ReactNode;
    isLoading?: boolean;
    itemsLoading?: boolean;
}

const Navigation: React.FC<NavigationProps> = observer((props) => {
    const store = props.store;
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavItemClick = useCallback(
        (item: INavigation) => {
            const u = item.link;
            store.currentNavItem = item;
            if (location.pathname !== u) {
                navigate(u);
            }
        },
        [location.pathname, navigate, store],
    );

    const navItems = store.itemGroups ? (
        store.itemGroups.map((val) => (
            <AcxExpansion
                expanded={val.expanded ?? false}
                header={<div style={groupHeaderStyle(theme)}>{val.header}</div>}
                key={`side-nav-${val.header}`}
                body={val.items.map((val, index) => (
                    <NavigationLink
                        key={`side-nav-item-${val.title}-${index}`}
                        val={val}
                        store={store}
                        handleNavItemClick={handleNavItemClick}
                        itemOptions={props.itemOptions}
                    />
                ))}
                bodyStyle={itemNavigationBodyStyle(theme)}
                rootStyle={itemNavigationRootStyle(theme)}
            />
        ))
    ) : (
        <div
            style={{
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
            }}
        >
            {store.items?.map((val) => (
                <NavigationLink
                    key={`side-nav-item-${val.title}`}
                    val={val}
                    store={store}
                    handleNavItemClick={handleNavItemClick}
                    itemOptions={props.itemOptions}
                />
            ))}
        </div>
    );

    React.useEffect(() => {
        if (location.pathname !== store.currentNavItem?.link) {
            let item;
            if (store.itemGroups) {
                store.itemGroups?.forEach((value, index) => {
                    value.items.forEach((val, i) => {
                        if (val.link === location.pathname) {
                            item = val;
                        }
                    });
                });
            } else {
                store.items?.forEach((val, i) => {
                    if (val.link === location.pathname) {
                        item = val;
                    }
                });
            }

            if (item) {
                store.currentNavItem = item;
            } else {
                if (store.defaultRoute) {
                    navigate(store.defaultRoute?.link, { replace: true });
                } else {
                    store.currentNavItem = undefined;
                }
            }
        }
    }, [
        location.pathname,
        navigate,
        store,
        store.currentNavItem,
        store.defaultRoute,
        store.itemGroups,
        store.items,
    ]);

    return (
        <RootDiv>
            {props.isLoading ? (
                <AcxLoadingIndicator
                    color="secondary"
                    alternate="PuffLoader"
                    size={125}
                />
            ) : (
                <>
                    <Scrollbars autoHide>
                        {props.controls}
                        {props.itemsLoading ? (
                            <AcxLoadingIndicator
                                color="secondary"
                                alternate="PuffLoader"
                                size={60}
                            />
                        ) : (
                            navItems
                        )}
                    </Scrollbars>
                </>
            )}
        </RootDiv>
    );
});

export default Navigation;
